/**
 * dietCalc — calculadora nutricional del plan.
 *
 * Calcula el gasto calórico (TDEE) con Mifflin-St Jeor × factor de actividad,
 * aplica el ajuste de objetivo (déficit / superávit) y reparte los macros:
 * proteína y grasa por kg de peso (lo decide el coach), carbohidratos = el
 * resto de las calorías.
 *
 * Valores de referencia estándar — punto de partida editable, no clínico.
 */
import type { DietActivity, DietAdjustMode, DietCalculator, DietSex } from "../types/plan";

/** Factores de actividad para TDEE = BMR × factor. */
export const ACTIVITY_FACTORS: Record<DietActivity, number> = {
  sedentary: 1.2,
  light: 1.375,
  moderate: 1.55,
  intense: 1.725,
  athlete: 1.9,
};

export const ACTIVITY_LABELS: Record<DietActivity, string> = {
  sedentary: "Sedentario",
  light: "Ligero (1-3 días/sem)",
  moderate: "Moderado (3-5 días/sem)",
  intense: "Intenso (6-7 días/sem)",
  athlete: "Atleta (2x/día)",
};

export const ADJUST_MODE_LABELS: Record<DietAdjustMode, string> = {
  maintain: "Mantener",
  deficit: "Déficit",
  surplus: "Superávit",
};

/** Magnitud (%) de cada preset, por modo. El signo lo da el modo. */
const PRESET_PCT: Record<"deficit" | "surplus", Record<"light" | "recommended" | "aggressive", number>> = {
  deficit: { light: 10, recommended: 20, aggressive: 25 },
  surplus: { light: 5, recommended: 10, aggressive: 15 },
};

export const ADJUST_PRESET_LABELS: Record<string, string> = {
  light: "Suave",
  recommended: "Recomendado",
  aggressive: "Agresivo",
  custom: "Personalizado",
};

/** % de ajuste efectivo (negativo = déficit, positivo = superávit, 0 = mantener). */
export function adjustPctFor(calc: DietCalculator): number {
  if (calc.adjustMode === "maintain") return 0;
  const sign = calc.adjustMode === "deficit" ? -1 : 1;
  if (calc.adjustPreset === "custom") {
    return sign * Math.abs(calc.adjustPct || 0);
  }
  const mag = PRESET_PCT[calc.adjustMode][calc.adjustPreset] ?? 0;
  return sign * mag;
}

export interface DietTargets {
  bmr: number;
  tdee: number;
  /** % de ajuste aplicado (negativo = déficit). */
  adjustPct: number;
  targetKcal: number;
  proteinG: number;
  fatG: number;
  carbsG: number;
  proteinKcal: number;
  fatKcal: number;
  carbsKcal: number;
  /** false si faltan datos clave (peso/altura/edad) — el cálculo no es fiable. */
  valid: boolean;
}

/**
 * Calcula objetivo calórico + macros a partir de la config de la calculadora.
 * BMR por Mifflin-St Jeor: 10·peso + 6.25·altura − 5·edad + (5 si M, −161 si F).
 */
export function computeDietTargets(calc: DietCalculator): DietTargets {
  const w = Number(calc.weightKg) || 0;
  let h = Number(calc.heightCm) || 0;
  // Defensa: si la altura vino en metros (ej. 1.8) en lugar de cm, la
  // normalizamos. Cubre datos viejos cargados mal antes del fix del form.
  if (h > 0 && h < 3) h = h * 100;
  const age = Number(calc.age) || 0;
  const valid = w > 0 && h > 0 && age > 0;

  const sexConst = calc.sex === "F" ? -161 : 5;
  const bmr = 10 * w + 6.25 * h - 5 * age + sexConst;
  const tdee = bmr * (ACTIVITY_FACTORS[calc.activity] ?? 1.55);
  const adjustPct = adjustPctFor(calc);
  const targetKcal = Math.max(0, tdee * (1 + adjustPct / 100));

  const proteinG = (Number(calc.proteinPerKg) || 0) * w;
  const fatG = (Number(calc.fatPerKg) || 0) * w;
  const proteinKcal = proteinG * 4;
  const fatKcal = fatG * 9;
  const carbsKcal = Math.max(0, targetKcal - proteinKcal - fatKcal);
  const carbsG = carbsKcal / 4;

  return {
    bmr,
    tdee,
    adjustPct,
    targetKcal,
    proteinG,
    fatG,
    carbsG,
    proteinKcal,
    fatKcal,
    carbsKcal,
    valid,
  };
}

/** Calculadora por defecto (deshabilitada) — se usa al "Activar calculadora". */
export function defaultDietCalculator(seed?: Partial<DietCalculator>): DietCalculator {
  return {
    enabled: true,
    weightKg: 0,
    heightCm: 0,
    age: 0,
    sex: "M" as DietSex,
    activity: "moderate",
    adjustMode: "maintain",
    adjustPreset: "recommended",
    adjustPct: 15,
    proteinPerKg: 1.8,
    fatPerKg: 0.8,
    ...seed,
  };
}
